import { isDeviceWithDynamicIsland, isDeviceWithNotch } from '@/utils/device';
import { Dimensions, Platform, StatusBar } from 'react-native';

/**
 * Responsive Scaling System
 * Scales design values from Figma base to actual device dimensions.
 * Uses mobile base (375) on phones and tablet base (1024) on tablets.
 */

export const DESIGN_BASE = {
  width: 375,
  height: 812,
  widthTablet: 1024,
  heightTablet: 768,
} as const;

/** Min short edge to treat device as tablet (portrait width or landscape height) */
export const TABLET_BREAKPOINT = 768;

export type ScaleSize = number | { phone: number; tablet: number };

/**
 * Computes all scaling metrics from a given width & height.
 * Pure function — safe to call from hooks or module scope.
 *
 * Tablet layout is only active when the device is a tablet AND in landscape.
 * Tablet in portrait and all phones use the mobile (375) design base.
 */
export function computeScaling(deviceWidth: number, deviceHeight: number) {
  // const isTabletDevice =
  //   isTabletDeviceInfo() || Math.min(deviceWidth, deviceHeight) >= TABLET_BREAKPOINT;
  const isLandscape = deviceWidth > deviceHeight;
  const isTab = isLandscape;

  const designWidth = isTab ? DESIGN_BASE.widthTablet : DESIGN_BASE.width;
  const designHeight = isTab ? DESIGN_BASE.heightTablet : DESIGN_BASE.height;

  const scaleWidth = deviceWidth / designWidth;
  const scaleHeight = deviceHeight / designHeight;
  const scaleFactor = Math.min(scaleWidth, scaleHeight);

  return {
    isTablet: isTab,
    designWidth,
    designHeight,
    scaleWidth,
    scaleHeight,
    scaleFactor,
    deviceWidth,
    deviceHeight,
  } as const;
}

// ---------- Static values (backward-compatible, captured once at load) ----------

const { width: DEVICE_WIDTH, height: DEVICE_HEIGHT } = Dimensions.get('window');

const STATIC = computeScaling(DEVICE_WIDTH, DEVICE_HEIGHT);

const SCALE = STATIC.scaleFactor;
const IS_TABLET = STATIC.isTablet;

/**
 * Scale a value from design base to device (width-based).
 * Static version — uses dimensions captured at module load.
 * For rotation-aware scaling, use `useScaling()` hook instead.
 * @deprecated Use `useScaling()` hook instead.
 */
export const scale = (size: ScaleSize): number => {
  if (typeof size === 'number') {
    return size * SCALE;
  }
  return IS_TABLET ? size.tablet * SCALE : size.phone * SCALE;
};

/** True when scaling uses tablet base (1024x768). */
export const isTablet = IS_TABLET;

export class Device {
  static readonly designBase = DESIGN_BASE;

  static readonly designWidth = STATIC.designWidth;
  static readonly designHeight = STATIC.designHeight;

  static readonly width = DEVICE_WIDTH;
  static readonly height = DEVICE_HEIGHT;

  static readonly scale = SCALE;
  static readonly scaleWidth = STATIC.scaleWidth;
  static readonly scaleHeight = STATIC.scaleHeight;

  static readonly statusBarHeight = Platform.select({
    ios: isDeviceWithDynamicIsland() ? 54 : isDeviceWithNotch() ? 47 : 20,
    android: StatusBar.currentHeight,
  }) as number;

  static readonly bottomHeight = isDeviceWithNotch() || isDeviceWithDynamicIsland() ? 34 : 10;

  static readonly isAndroid = Platform.OS === 'android';
  static readonly isIOS = Platform.OS === 'ios';
  static readonly isTablet = IS_TABLET;
  static readonly isSmall = !IS_TABLET && DEVICE_WIDTH < DESIGN_BASE.width;
  static readonly isLarge = !IS_TABLET && DEVICE_WIDTH > DESIGN_BASE.width * 1.2;
  static readonly hasNotch = isDeviceWithNotch() || isDeviceWithDynamicIsland();

  private constructor() {}
}
